/*!
// <copyright file="Dialogs.js" company="Microsoft">
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
*/

import { util } from "@ax/office-shared-lib";
import { appElements } from "./Elements";

export namespace dialogs {

    /** Handles opening and closing dialogs for the app. */
    export class DialogHandler {

        /** The dialog container element that contains all dialogs. */
        private _dialogContainer: JQuery;

        /** The stack of IDs for dialogs currently being handled. */
        private _dialogStack: string[];

        /** The IDs of controls relating to dialogs required by the dialog handler. */
        private static ControlIDs = {

            /** ID of the dialog container element. */
            dialogContainer: "dialogContent"
        };

        constructor() {
            /* jshint validthis: true */
            this._dialogStack = [];
        }

        /** Initializes the dialog handler. */
        public initialize(): void {
            this._dialogContainer = util.findElementById(DialogHandler.ControlIDs.dialogContainer, appElements.contentDiv);
        }

        /**
         * Opens the dialog corresponding to the provided ID.
         * @param dialogID The ID of the dialog to show.
         * @returns True if the dialog is found and opened.
         */
        public openDialog(dialogID: string): boolean {
            var dialog = util.findElementById(dialogID, this._dialogContainer);
            var dialogFound = util.doesElementExist(dialog);

            // If the dialog is found work on showing it
            if (dialogFound) {

                // If the handler is already handling another dialog, be sure to deactivate the current active dialog
                if (this._dialogStack.length > 0) {
                    var activeDialog = util.findElementById(this._dialogStack[this._dialogStack.length - 1], this._dialogContainer);
                    if (util.doesElementExist(activeDialog)) {
                        util.changeAttribute(activeDialog, appElements.GeneralAttributes.isActive, false);
                    }
                }

                util.changeAttribute(dialog, appElements.GeneralAttributes.isActive, true);
                util.changeAttribute(appElements.contentDiv, appElements.ContentAttributes.hasActiveDialog, true);
                util.setFocus(dialog);

                this._dialogStack.push(dialogID);
            }

            return dialogFound;
        }

        /**
         * Closes the active dialog, optionally verifying the active dialog matches the provided dialog ID before closing.
         * @param dialogID The ID of the dialog expected to be the active dialog.
         * @returns True if the dialog is found, able to be closed, and optionally matches the provided dialog ID.
         */
        public closeActiveDialog(dialogID?: string): boolean {

            // If the optional dialog ID wasn't specified and there is at least one item on the stack, set the dialog ID to the active dialog
            if (dialogID === undefined && this._dialogStack.length > 0) {
                dialogID = this._dialogStack[this._dialogStack.length - 1];
            }

            // Determine if the active dialog is the expected dialog by ensuring there is an active dialog and comparing it to the provided ID
            var expectedActiveDialog = this._dialogStack.length > 0 && this._dialogStack[this._dialogStack.length - 1] === dialogID;

            // If the active dialog matches the provided ID, attempt to find the active dialog and verify it is found
            var dialog;
            if (expectedActiveDialog) {
                dialog = util.findElementById(dialogID, this._dialogContainer);
                expectedActiveDialog = util.doesElementExist(dialog);
            }

            // If the active dialog is found and matches the provided ID, attempt to close it
            if (expectedActiveDialog) {

                // Remove the active dialog from the stack and adjust its attributes to indicate it is no longer active
                this._dialogStack.pop();
                util.changeAttribute(dialog, appElements.GeneralAttributes.isActive, false);

                // If the dialog stack has another dialog active after the previously active dialog was removed, attempt to make that dialog active;
                // otherwise indicate to the content div that there are no longer any dialogs active
                if (this._dialogStack.length > 0) {
                    var nextDialogID = this._dialogStack[this._dialogStack.length - 1];
                    var nextDialog = util.findElementById(nextDialogID, this._dialogContainer);

                    // If the next dialog was found, adjust its attributes to indicate it is now active, otherwise adjust the content div to indicate it no longer has any dialogs active
                    if (util.doesElementExist(nextDialog)) {
                        util.changeAttribute(nextDialog, appElements.GeneralAttributes.isActive, true);
                        util.setFocus(nextDialog);
                    } else {
                        util.changeAttribute(appElements.contentDiv, appElements.ContentAttributes.hasActiveDialog, false);
                        util.setFocus(appElements.contentDiv);
                    }
                } else {
                    util.changeAttribute(appElements.contentDiv, appElements.ContentAttributes.hasActiveDialog, false);
                    util.setFocus(appElements.contentDiv);
                }
            }

            return expectedActiveDialog;
        }
    }

    /** The handler for dialogs. */
    export let handler: dialogs.DialogHandler;
}
